import { Eye, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useEmailDetail } from '@/hooks/useEmailsData';
import { formatRecipients, getStatusBadgeVariant, TEMPLATE_TYPE_LABELS } from '@/lib/email-display';
import { formatDateTime } from '@/lib/utils';

interface ViewEmailDetailButtonProps {
  emailId: number;
  emailChannel: string;
}

/** Icon-only trigger that drills from an activity entry into the full email detail. */
export function ViewEmailDetailButton({ emailId, emailChannel }: ViewEmailDetailButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="View email details"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>View email details</TooltipContent>
      </Tooltip>
      <EmailDetailModal open={open} onOpenChange={setOpen} emailId={emailId} emailChannel={emailChannel} />
    </>
  );
}

interface EmailDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailId: number | null;
  emailChannel: string | null;
}

export function EmailDetailModal({ open, onOpenChange, emailId, emailChannel }: EmailDetailModalProps) {
  const {
    data: emailDetail,
    isLoading,
    isError,
    refetch,
  } = useEmailDetail(open ? emailId : null, open ? emailChannel : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email Details</DialogTitle>
          <DialogDescription>{emailDetail ? emailDetail.subject : 'Loading...'}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <p className="text-sm text-muted-foreground">Failed to load the email details.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : emailDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <div className="mt-1">
                  <Badge variant={getStatusBadgeVariant(emailDetail.status)}>{emailDetail.status}</Badge>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Source</Label>
                <div className="mt-1">
                  <Badge variant="outline">{emailDetail.sourceType}</Badge>
                  {!!emailDetail.sourceId && (
                    <span className="ml-2 text-sm text-muted-foreground">#{emailDetail.sourceId}</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground">Subject</Label>
              <p className="mt-1 font-medium">{emailDetail.subject}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">To</Label>
                <p className="mt-1">{formatRecipients(emailDetail.toRecipients)}</p>
              </div>
              {!!emailDetail.ccRecipients && (
                <div>
                  <Label className="text-muted-foreground">CC</Label>
                  <p className="mt-1">{formatRecipients(emailDetail.ccRecipients)}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Customer</Label>
                <p className="mt-1">
                  {emailDetail.customerName}
                  {!!emailDetail.customerAccountNumber && (
                    <span className="text-muted-foreground ml-1">({emailDetail.customerAccountNumber})</span>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Template</Label>
                <p className="mt-1">{emailDetail.templateName ?? '-'}</p>
              </div>
            </div>

            {emailDetail.templateTypeId !== null && (
              <div>
                <Label className="text-muted-foreground">Template Type</Label>
                <p className="mt-1">
                  <Badge variant="outline">
                    {TEMPLATE_TYPE_LABELS[emailDetail.templateTypeId] ?? emailDetail.templateTypeId}
                  </Badge>
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Sent By</Label>
                <p className="mt-1">
                  {emailDetail.sentByName}
                  {!!emailDetail.sentByEmail && (
                    <span className="text-muted-foreground ml-1">({emailDetail.sentByEmail})</span>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">{emailDetail.sentAt ? 'Sent At' : 'Created At'}</Label>
                <p className="mt-1">{formatDateTime(emailDetail.sentAt ?? emailDetail.createdAt)}</p>
              </div>
            </div>

            {emailDetail.attachedInvoiceReferences.length > 0 && (
              <div>
                <Label className="text-muted-foreground">Attached Invoices</Label>
                <p className="mt-1">{emailDetail.attachedInvoiceReferences.join(', ')}</p>
              </div>
            )}

            {!!emailDetail.errorMessage && (
              <div>
                <Label className="text-destructive">Error Message</Label>
                <p className="mt-1 text-destructive text-sm rounded-md border border-destructive/20 bg-destructive/5 p-3">
                  {emailDetail.errorMessage}
                </p>
              </div>
            )}

            {!!emailDetail.htmlBody && (
              <div>
                <Label className="text-muted-foreground">Email Body</Label>
                <div className="mt-1 rounded-md border overflow-hidden">
                  <iframe
                    srcDoc={emailDetail.htmlBody}
                    sandbox=""
                    title="Email body preview"
                    className="w-full min-h-[300px] bg-white"
                    style={{ border: 'none' }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
